import { useRef, useState } from 'react';
import { useQueries, useQuery } from 'react-query';
import { useDebouncedValue } from '@shared/hooks';
import { IUserSelectOption } from '../../interfaces';
import { UsersService } from '../../services';

export const USER_SEARCH_MIN_LENGTH = 2;
const DEBOUNCE_MS = 500;
const STALE_TIME = 5 * 60 * 1000;

type SelectedValue = string | number | null | undefined;

export type UseUserSelectSearchParams = {
  selected?: SelectedValue | SelectedValue[];
  query?: Record<string, any>;
  enabled?: boolean;
  preload?: boolean;
};

const toArray = <T>(value: T | T[] | null | undefined): T[] => (value == null ? [] : Array.isArray(value) ? value : [value]);

const formatLabel = (id: string | number, name: string) => `#${id} - ${name}`;

export const useUserSelectSearch = ({ selected, query, enabled = true, preload = false }: UseUserSelectSearchParams = {}) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), DEBOUNCE_MS);
  const isSearchable = debouncedSearch.length >= USER_SEARCH_MIN_LENGTH;

  const labels = useRef(new Map<string, string>());

  const searchResult = useQuery(
    ['users', 'select', 'search', query ?? {}, debouncedSearch],
    () => UsersService.searchSelectUsers(debouncedSearch, query).then((r) => (r.status === 200 ? r.data : [])),
    {
      enabled: enabled && isSearchable,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  const preloadResult = useQuery(['users', 'select', 'list', query ?? {}], () => UsersService.searchSelectUsers('', query).then((r) => (r.status === 200 ? r.data : [])), {
    enabled: enabled && preload,
    staleTime: STALE_TIME,
  });

  const cache = (users: IUserSelectOption[]) => users.forEach((user) => labels.current.set(String(user.id), formatLabel(user.id, user.name)));

  cache(searchResult.data ?? []);
  cache(preloadResult.data ?? []);

  const selectedValues = toArray(selected)
    .filter((value) => value !== null && value !== undefined && value !== '')
    .map(String);

  const unresolved = selectedValues.filter((value) => !labels.current.has(value));

  const resolved = useQueries(
    unresolved.map((value) => ({
      queryKey: ['users', 'select', 'resolve', value],
      queryFn: () => UsersService.searchSelectUsers(value).then((r) => (r.status === 200 ? r.data : [])),
      enabled,
      staleTime: STALE_TIME,
    })),
  );

  resolved.forEach((result) => cache((result.data as IUserSelectOption[]) ?? []));

  const found = isSearchable ? (searchResult.data ?? []) : preload ? (preloadResult.data ?? []) : [];
  const foundIds = new Set(found.map((user) => String(user.id)));

  const options = found.map((user) => ({ value: String(user.id), label: formatLabel(user.id, user.name) }));

  selectedValues.filter((value) => !foundIds.has(value)).forEach((value) => options.unshift({ value, label: labels.current.get(value) ?? `#${value}` }));

  return {
    options,
    search,
    setSearch,
    isSearchable,
    hasOptionsWithoutSearch: preload,
    isLoading: isSearchable ? searchResult.isFetching : preload && preloadResult.isFetching,
  };
};
